export type ArticulationStance = {
  id: string;
  label: string;
  line: string;
  landing: string;
  protects: string;
  tradeoff: string;
};

export type ExpressionStudy = {
  id: string;
  number: string;
  title: string;
  provenance: "Original Verbalore";
  craft: string[];
  size: "quick" | "standard" | "deep";
  minutes: number;
  context: string;
  skill: string;
  situation: string;
  tangledDraft: string;
  intention: string;
  invariant: string;
  concept: string;
  practiceCue: string;
  stances: ArticulationStance[];
};

export const BOUNDARY_STUDY: ExpressionStudy = {
  id: "boundary-not-favour",
  number: "01",
  title: "A boundary, not a favour",
  provenance: "Original Verbalore",
  craft: ["compression", "reciprocity", "controlled authority"],
  size: "quick",
  minutes: 3,
  context: "Workplace conversation",
  skill: "Protecting a turn",
  situation:
    "A colleague repeatedly interrupts you in a meeting. You want to finish your point without sounding hostile or asking permission to speak.",
  tangledDraft:
    "I was just wondering if maybe I could finish what I was saying first, if that’s okay?",
  intention: "Protect your turn while leaving room for the other person to respond.",
  invariant: "You will finish the point first. Their response still matters.",
  concept: "Confidence is not volume. It is clarity without unnecessary permission.",
  practiceCue: "State the order of turns in one line while keeping the response welcome.",
  stances: [
    {
      id: "direct",
      label: "Direct",
      line: "Let me finish the point before you respond.",
      landing: "Clear and immediate",
      protects: "The order of turns is unmistakable.",
      tradeoff: "It leaves little warmth in the moment.",
    },
    {
      id: "tactful",
      label: "Tactful",
      line: "I’ll complete the thought, then I want to hear your response.",
      landing: "Firm with reciprocity",
      protects: "The boundary and the other person’s dignity remain visible.",
      tradeoff: "The second clause makes it slightly less compressed.",
    },
    {
      id: "warm-authority",
      label: "Warm authority",
      line: "Give me a moment to finish this, and then I’m with you.",
      landing: "Human and composed",
      protects: "Connection survives the correction.",
      tradeoff: "Warmth can soften the firmness if your delivery wavers.",
    },
    {
      id: "restrained",
      label: "Restrained",
      line: "I have not finished the point. I will hear you when I have.",
      landing: "Quiet and controlled",
      protects: "You state the fact without escalating the exchange.",
      tradeoff: "The next action is implied rather than fully stated.",
    },
    {
      id: "punchy",
      label: "Punchy",
      line: "Let me land the point. Then it’s yours.",
      landing: "Brief and memorable",
      protects: "The sequence carries force without a long explanation.",
      tradeoff: "Its idiom may feel too sharp in a formal room.",
    },
  ],
};

export const HOMEPAGE_RANGE = {
  provenance: "Original Verbalore",
  intention: "Tell a colleague their draft cannot ship yet and needs another pass.",
  invariant: "The draft is not ready. More work is required. You are making the call.",
  stances: [
    {
      id: "direct",
      label: "Direct",
      line: "This isn’t ready. It needs another pass before we ship.",
      landing: "The verdict arrives without delay.",
    },
    {
      id: "tactful",
      label: "Tactful",
      line: "This needs another pass before it is ready to ship.",
      landing: "The work is corrected without diminishing its author.",
    },
    {
      id: "warm-authority",
      label: "Warm authority",
      line: "I want us to ship this with confidence. It needs another pass before I can approve it.",
      landing: "Encouragement carries the decision instead of replacing it.",
    },
    {
      id: "restrained",
      label: "Restrained",
      line: "We are not there yet. It needs another round before it ships.",
      landing: "The refusal stays measured and leaves little friction.",
    },
    {
      id: "punchy",
      label: "Punchy",
      line: "Not yet. Another pass before this can ship.",
      landing: "Compression gives the decision momentum.",
    },
  ],
} as const;

export const EXPRESSION_STUDIES: ExpressionStudy[] = [
  {
    id: "disagree-without-a-fight",
    number: "02",
    title: "Disagree without a fight",
    provenance: "Original Verbalore",
    craft: ["dissent", "position-holding", "non-escalation"],
    size: "standard",
    minutes: 7,
    context: "Team meetings and group decisions",
    skill: "Registering disagreement without turning it into conflict",
    situation:
      "In a planning discussion, someone proposes a direction you think is wrong. You want to say you disagree, and why, without pretending to be persuaded and without making it personal.",
    tangledDraft:
      "I mean, you might be right, I don’t know, but I guess I’m maybe not totally sure this is the way to go?",
    intention: "Register real disagreement with the proposal while keeping the working relationship intact.",
    invariant: "You disagree with the proposal, and you are not hiding it. The relationship continues.",
    concept: "Disagreement is information, not an attack. Naming it clearly is a form of respect.",
    practiceCue: "Read each line and delete any word that apologises for disagreeing. The objection should survive the cut.",
    stances: [
      {
        id: "direct",
        label: "Direct",
        line: "I don’t agree with this. Here’s where I think it breaks.",
        landing: "Plain and unhedged",
        protects: "Your actual position goes on the record without spin.",
        tradeoff: "It offers no cushion for someone expecting consensus.",
      },
      {
        id: "tactful",
        label: "Tactful",
        line: "I see the appeal, and I still land somewhere different. Let me show you why.",
        landing: "Heard, then held",
        protects: "The other view is acknowledged before yours arrives.",
        tradeoff: "The softening reads as hedging if you rush the reason.",
      },
      {
        id: "warm-authority",
        label: "Warm authority",
        line: "I’m going to push back on this one, because I think we can do better.",
        landing: "Loyal opposition",
        protects: "The dissent reads as investment, not opposition.",
        tradeoff: "Warmth can blur how firm the objection really is.",
      },
      {
        id: "restrained",
        label: "Restrained",
        line: "This is not a call I can support.",
        landing: "Quietly unmoved",
        protects: "You hold the position without opening an argument.",
        tradeoff: "The reasons stay implied, so it can be brushed aside.",
      },
      {
        id: "punchy",
        label: "Punchy",
        line: "I disagree. And I think it matters.",
        landing: "Short and certain",
        protects: "It leaves no doubt where you stand.",
        tradeoff: "Without the reasoning it can feel like a wall.",
      },
    ],
  },
  {
    id: "a-no-that-stays-no",
    number: "03",
    title: "A no that stays no",
    provenance: "Original Verbalore",
    craft: ["refusal", "clean refusal", "concision"],
    size: "standard",
    minutes: 6,
    context: "Requests you need to turn down",
    skill: "Declining cleanly, without a trail of apology or a false maybe",
    situation:
      "A colleague asks you to take on something that isn’t yours to carry. You need to decline so it is heard as a real no, not a soft maybe you’ll have to walk back later.",
    tangledDraft:
      "Oh, um, I’d love to help, and normally I would, so maybe I can try to squeeze it in, we’ll see?",
    intention: "Decline the request clearly so it is understood as final.",
    invariant: "The answer is no, and it is final. You are not leaving it open.",
    concept: "A clean no protects everyone’s time. A blurry no just books the same conversation twice.",
    practiceCue: "Say the no out loud, then stop. Notice the urge to keep talking, and let the silence finish it.",
    stances: [
      {
        id: "direct",
        label: "Direct",
        line: "No, I can’t take this on.",
        landing: "Flat no",
        protects: "The answer is unmistakable.",
        tradeoff: "It gives the other person nothing to do next.",
      },
      {
        id: "tactful",
        label: "Tactful",
        line: "I can’t take this on, but I can point you to who can.",
        landing: "No, with a door",
        protects: "The no stands while a path forward stays open.",
        tradeoff: "The offer distracts if they hear only the second half.",
      },
      {
        id: "warm-authority",
        label: "Warm authority",
        line: "This one’s a no from me, and I’d rather tell you straight than string you along.",
        landing: "Honest and kind",
        protects: "The honesty reads as respect for their time.",
        tradeoff: "The extra clause adds length to a simple answer.",
      },
      {
        id: "restrained",
        label: "Restrained",
        line: "I won’t be able to do that.",
        landing: "Soft but final",
        protects: "It closes the request without any friction.",
        tradeoff: "Mild phrasing can invite a second ask.",
      },
      {
        id: "punchy",
        label: "Punchy",
        line: "Not this one. It’s not mine to carry.",
        landing: "Quick and closed",
        protects: "The refusal and the reason land together.",
        tradeoff: "The idiom can feel curt in writing.",
      },
    ],
  },
  {
    id: "correct-dont-crush",
    number: "04",
    title: "Correct, don’t crush",
    provenance: "Original Verbalore",
    craft: ["correction", "face-saving", "problem-focus"],
    size: "standard",
    minutes: 8,
    context: "Correcting someone whose standing you want to protect",
    skill: "Naming an error without shrinking the person who made it",
    situation:
      "Someone on your team has made an error others will notice. You need it corrected before it goes out, without turning the fix into a public shaming.",
    tangledDraft:
      "So this is probably my fault for not being clear, but there might be a tiny thing with the numbers, no big deal though.",
    intention: "Name the error and get it fixed while leaving the person’s standing intact.",
    invariant: "The mistake is named and must be corrected. The person is not diminished.",
    concept: "Direct the pressure at what needs fixing, not at the person fixing it. Precision can be firm without becoming personal.",
    practiceCue: "For each line, ask whether it is hard on the problem or hard on the person. Keep the first, cut the second.",
    stances: [
      {
        id: "direct",
        label: "Direct",
        line: "There’s an error in the figures. Let’s fix it before this goes out.",
        landing: "Problem first",
        protects: "The fix is the focus, not the blame.",
        tradeoff: "In front of others even a neutral tone can sting.",
      },
      {
        id: "tactful",
        label: "Tactful",
        line: "The figures need another look before we send.",
        landing: "Shared second look",
        protects: "The correction lands as a check, not an accusation.",
        tradeoff: "The softening can understate how much it matters.",
      },
      {
        id: "warm-authority",
        label: "Warm authority",
        line: "Let’s get this right before it goes out. The figures need another pass.",
        landing: "Shared standard, then correction",
        protects: "The shared goal is named before the additional work.",
        tradeoff: "Collaborative wording can blur who must act if responsibility is unclear.",
      },
      {
        id: "restrained",
        label: "Restrained",
        line: "The figures need correcting before we send this.",
        landing: "Invited, not accused",
        protects: "It asks for the fix without assigning fault.",
        tradeoff: "The vagueness can slow the fix down.",
      },
      {
        id: "punchy",
        label: "Punchy",
        line: "Figures are off. They need fixing before this ships.",
        landing: "Urgency first",
        protects: "The required action is unmistakable.",
        tradeoff: "The clipped delivery can feel cold to someone already anxious.",
      },
    ],
  },
  {
    id: "say-what-you-want",
    number: "05",
    title: "Say what you want",
    provenance: "Original Verbalore",
    craft: ["clear request", "de-hedging", "brevity"],
    size: "quick",
    minutes: 4,
    context: "Asking directly for what you want",
    skill: "Making a request clear enough to get a straight answer",
    situation:
      "You need something from someone and keep wrapping it in qualifiers. You want to make the ask plainly so it can actually be answered.",
    tangledDraft:
      "I don’t want to be a bother, and only if you have time, but I was sort of hoping maybe you could possibly cover Friday?",
    intention: "Make the request clear enough to get a straight yes or no.",
    invariant: "The ask is stated outright and can be answered directly.",
    concept: "A buried ask hands the other person the work of guessing. Clarity is a courtesy.",
    practiceCue: "Strip the qualifiers off the front of each line. Read what’s left and check it can still be answered yes or no.",
    stances: [
      {
        id: "direct",
        label: "Direct",
        line: "I’d like you to cover the Friday session. Can you?",
        landing: "Straight ask",
        protects: "The request is specific and answerable.",
        tradeoff: "It leaves little room to save face if they’d rather not.",
      },
      {
        id: "tactful",
        label: "Tactful",
        line: "Would you be able to cover Friday’s session? It would really help.",
        landing: "Clear, easy out",
        protects: "The ask stays clear while giving them an easy exit.",
        tradeoff: "The added reason can read as gentle pressure.",
      },
      {
        id: "warm-authority",
        label: "Warm authority",
        line: "I’d love you to cover Friday’s session. Are you in?",
        landing: "An invitation",
        protects: "The ask feels like an opportunity, not a burden.",
        tradeoff: "Charm can obscure that it’s still a real request.",
      },
      {
        id: "restrained",
        label: "Restrained",
        line: "Are you free to take Friday’s session?",
        landing: "Plain question",
        protects: "A plain question, easy to answer either way.",
        tradeoff: "The understatement can hide how much you want a yes.",
      },
      {
        id: "punchy",
        label: "Punchy",
        line: "Friday session. You in?",
        landing: "Nothing to decode",
        protects: "There is nothing to decode and it’s quick to answer.",
        tradeoff: "Too casual for a big or sensitive ask.",
      },
    ],
  },
  {
    id: "the-truth-whole",
    number: "06",
    title: "The truth, whole",
    provenance: "Original Verbalore",
    craft: ["honesty", "care under truth", "candour"],
    size: "deep",
    minutes: 11,
    context: "Telling someone close a hard truth they asked for",
    skill: "Delivering the whole truth without softening it into a lie",
    situation:
      "A friend is pinning their hopes on a project that is failing, and they have asked whether more time will save it. Your honest assessment is that more time is unlikely to change the outcome. You want to tell them without cushioning the truth into a falsehood or delivering it coldly.",
    tangledDraft:
      "I mean, it could still work out, you never know, these things take time, so maybe don’t give up, but also who knows really.",
    intention: "Tell the person the hard truth without softening it into something untrue.",
    invariant: "The project is failing, and more time is unlikely to change that. The assessment is not blurred into false hope.",
    concept: "Kindness lives in how you tell the truth, not in whether you tell it. Withholding it is its own harm.",
    practiceCue: "Say the truth first, then the care. If the care changes the truth, you’ve softened too far.",
    stances: [
      {
        id: "direct",
        label: "Direct",
        line: "It isn’t working, and I don’t think more time will change that.",
        landing: "Clean and whole",
        protects: "They get reality with nothing to misread.",
        tradeoff: "Without a pause it can land harder than you mean.",
      },
      {
        id: "tactful",
        label: "Tactful",
        line: "I care about you, so I’ll be honest: I don’t think this is going to work.",
        landing: "Cared for, then told",
        protects: "The relationship frames the truth instead of blunting it.",
        tradeoff: "The preamble can make them brace before the point arrives.",
      },
      {
        id: "warm-authority",
        label: "Warm authority",
        line: "You asked me straight, so here it is: this is failing, and more time is unlikely to change it.",
        landing: "Owed to you",
        protects: "Respect for them carries the bad news.",
        tradeoff: "The framing draws attention to you as the messenger.",
      },
      {
        id: "restrained",
        label: "Restrained",
        line: "I don’t see this turning around, even with more time.",
        landing: "Quiet certainty",
        protects: "The truth stands without added weight.",
        tradeoff: "The quietness can let them minimize what you said.",
      },
      {
        id: "punchy",
        label: "Punchy",
        line: "It is not working. More time will not make it work.",
        landing: "Bare and true",
        protects: "The reason to trust it is built into the line.",
        tradeoff: "The brevity can feel abrupt for something this heavy.",
      },
    ],
  },
  {
    id: "the-exact-feeling",
    number: "07",
    title: "The exact feeling",
    provenance: "Original Verbalore",
    craft: ["emotional precision", "naming", "restraint"],
    size: "standard",
    minutes: 7,
    context: "Naming a feeling to someone close",
    skill: "Saying the exact feeling instead of a vague one",
    situation:
      "Someone close made a decision that affects you without asking you first. You feel hurt, not angry, and want to name both the feeling and its cause precisely enough that they can respond.",
    tangledDraft:
      "I’m fine, honestly, it’s nothing, I’m just a bit off maybe, don’t worry about it, it doesn’t matter.",
    intention: "Name what you actually feel precisely enough to be understood.",
    invariant: "You feel hurt because they made the decision without asking you. The feeling and cause remain intact.",
    concept: "A vague feeling earns a vague reply. Precision is what makes a feeling answerable.",
    practiceCue: "Replace the first feeling word you reach for with a more precise one. Notice how the response changes.",
    stances: [
      {
        id: "direct",
        label: "Direct",
        line: "I’m not angry. I’m hurt that you didn’t ask me first.",
        landing: "Named and exposed",
        protects: "The exact feeling and its cause are both visible.",
        tradeoff: "The precision leaves you exposed.",
      },
      {
        id: "tactful",
        label: "Tactful",
        line: "I’ve been sitting with this, and I feel hurt that you decided without asking me.",
        landing: "Sat with, then said",
        protects: "Naming the process makes the feeling easier to receive.",
        tradeoff: "The lead-in can delay the point.",
      },
      {
        id: "warm-authority",
        label: "Warm authority",
        line: "I want to be clear because it matters: I felt hurt when you decided without asking me.",
        landing: "Precise on purpose",
        protects: "The precision keeps it from escalating into the wrong fight.",
        tradeoff: "The framing can feel managed rather than raw.",
      },
      {
        id: "restrained",
        label: "Restrained",
        line: "I felt hurt when you decided without asking me, and it is worth saying.",
        landing: "Understated honesty",
        protects: "It is honest without dramatizing.",
        tradeoff: "The restraint can make it seem smaller than it is.",
      },
      {
        id: "punchy",
        label: "Punchy",
        line: "Not angry. Hurt that you chose without me.",
        landing: "Three beats",
        protects: "The distinction lands in three beats.",
        tradeoff: "The compression can read as clipped when the moment is tender.",
      },
    ],
  },
  {
    id: "move-them-dont-push",
    number: "08",
    title: "Move them, don’t push",
    provenance: "Original Verbalore",
    craft: ["persuasion", "honest persuasion", "freedom to refuse"],
    size: "standard",
    minutes: 8,
    context: "Moving someone toward a decision",
    skill: "Persuading by argument, leaving them free to refuse",
    situation:
      "You want someone to back a two-week pilot they are hesitant about. The pilot limits risk and gives both of you evidence before a larger commitment. You want to move them with that case, not pressure or flattery.",
    tangledDraft:
      "You probably won’t go for this, and it’s totally your call, but I sort of think maybe it could be worth considering, possibly?",
    intention: "Ask the person to back a two-week pilot because it limits risk and produces evidence, while leaving them free to refuse.",
    invariant: "The two-week pilot, limited risk, and evidence remain the case. The listener remains free to say no.",
    concept: "Real persuasion leaves the other person free to say no. That freedom is what makes their yes worth having.",
    practiceCue: "After each line, imagine them saying no. If the line only works when they can’t, it’s pressure, not persuasion.",
    stances: [
      {
        id: "direct",
        label: "Direct",
        line: "Back a two-week pilot. It limits the risk and gives us evidence before we commit.",
        landing: "Case first",
        protects: "The argument stands on its own merits.",
        tradeoff: "Leading with logic can feel like a challenge.",
      },
      {
        id: "tactful",
        label: "Tactful",
        line: "I know you have doubts. Would you consider a two-week pilot so we can test it without a full commitment?",
        landing: "Doubt respected",
        protects: "Their hesitation is respected while you still make the case.",
        tradeoff: "The deference can cost you momentum.",
      },
      {
        id: "warm-authority",
        label: "Warm authority",
        line: "Give this two weeks. We will limit the risk, learn from real evidence, and decide together after.",
        landing: "Confident welcome",
        protects: "The confidence invites them in rather than cornering them.",
        tradeoff: "Assuming agreement can put a wary person on guard.",
      },
      {
        id: "restrained",
        label: "Restrained",
        line: "A two-week pilot would give us evidence without committing us beyond it.",
        landing: "An open door",
        protects: "It opens the door without pushing.",
        tradeoff: "The understatement can fail to convey how much it matters.",
      },
      {
        id: "punchy",
        label: "Punchy",
        line: "Two weeks. Limited risk. Real evidence. Then you decide.",
        landing: "Drive and freedom",
        protects: "Energy and the freedom to refuse arrive together.",
        tradeoff: "The drive can feel like pressure to someone already wary.",
      },
    ],
  },
  {
    id: "sorry-not-smaller",
    number: "09",
    title: "Sorry, not smaller",
    provenance: "Original Verbalore",
    craft: ["apology", "accountability", "self-respect"],
    size: "deep",
    minutes: 12,
    context: "Owning a mistake that cost someone",
    skill: "Taking full responsibility without collapsing into self-blame",
    situation:
      "You got something wrong and it cost someone. You need to own it fully without collapsing into self-blame that makes them comfort you instead of hearing the apology.",
    tangledDraft:
      "I’m so sorry, I’m the worst, I always mess everything up, you must hate me, I understand if you never trust me again.",
    intention: "Take full responsibility for the mistake without diminishing yourself.",
    invariant: "You own the mistake completely, and you do not erase yourself doing it.",
    concept: "A real apology attends to the harm, not your guilt. Grovelling asks the injured person to manage your remorse as well.",
    practiceCue: "Read each line and ask who it comforts. If it comforts you, cut it. The apology is for them.",
    stances: [
      {
        id: "direct",
        label: "Direct",
        line: "I got this wrong, and it cost you. I own that.",
        landing: "Total and clean",
        protects: "Responsibility is complete and unqualified.",
        tradeoff: "The brevity can read as unmoved if your tone is flat.",
      },
      {
        id: "tactful",
        label: "Tactful",
        line: "That was my mistake, and I understand what it put you through. I’m sorry.",
        landing: "Owned and understood",
        protects: "Ownership and their experience both get named.",
        tradeoff: "Naming their side risks sounding like you’re managing them.",
      },
      {
        id: "warm-authority",
        label: "Warm authority",
        line: "I won’t make you manage my guilt. This was on me, and I am going to fix it.",
        landing: "Repair, not penance",
        protects: "Accountability points at repair, not self-punishment.",
        tradeoff: "Refusing to grovel can read as too composed to someone who wants remorse.",
      },
      {
        id: "restrained",
        label: "Restrained",
        line: "I was wrong here, and I’m sorry for it.",
        landing: "Plain apology",
        protects: "The apology stands without spectacle.",
        tradeoff: "Its plainness can seem to underweight the harm.",
      },
      {
        id: "punchy",
        label: "Punchy",
        line: "My fault. No excuses. I’ll make it right.",
        landing: "Owned in a breath",
        protects: "Ownership and the next step in one breath.",
        tradeoff: "The compression can feel like closing the subject too fast.",
      },
    ],
  },
  {
    id: "cut-to-the-point",
    number: "10",
    title: "Cut to the point",
    provenance: "Original Verbalore",
    craft: ["compression", "concision", "signal over padding"],
    size: "quick",
    minutes: 3,
    context: "Saying something in far fewer words",
    skill: "Cutting the excess while keeping the whole point",
    situation:
      "You need to tell the team the deadline moved to Thursday and the review is cancelled, and your draft has grown into three paragraphs. You want to say the same thing in a fraction of the words without losing what matters.",
    tangledDraft:
      "So I wanted to flag that after some back and forth we’ve decided to shift a few things around, and the upshot, if it helps, is that the timing has moved a little and we’re also going to reconsider one of the steps we usually run.",
    intention: "Tell the team the deadline is now Thursday and the review is off, in as few words as it takes.",
    invariant: "Both facts survive: Thursday, and no review. Only the excess is cut.",
    concept: "Length is not thoroughness. The extra words usually protect the writer, not the reader.",
    practiceCue: "Count the words in your draft, then in each line. Check that the shortest version still carries both facts.",
    stances: [
      {
        id: "direct",
        label: "Direct",
        line: "Deadline’s Thursday now. Review’s cancelled.",
        landing: "Both facts, no clutter",
        protects: "Both facts land with zero clutter.",
        tradeoff: "No context for anyone who needs the why.",
      },
      {
        id: "tactful",
        label: "Tactful",
        line: "Quick change: Thursday’s the new deadline, and we’re skipping the review.",
        landing: "Gentle on-ramp",
        protects: "Brevity with a soft opening.",
        tradeoff: "The opener adds a few words that the direct version omits.",
      },
      {
        id: "warm-authority",
        label: "Warm authority",
        line: "New plan: Thursday deadline, no review. You’ve got this.",
        landing: "Tight and sure",
        protects: "The facts plus a note of confidence, still tight.",
        tradeoff: "The reassurance is excess if the reader only wants facts.",
      },
      {
        id: "restrained",
        label: "Restrained",
        line: "Deadline moves to Thursday. No review this round.",
        landing: "Plain and complete",
        protects: "Plain, complete, and easy to scan.",
        tradeoff: "The flatness gives no signal of how big the change is.",
      },
      {
        id: "punchy",
        label: "Punchy",
        line: "Thursday. No review.",
        landing: "Maximum speed",
        protects: "Maximum speed with both facts intact.",
        tradeoff: "Too terse for readers who’ll want the reasoning.",
      },
    ],
  },
  {
    id: "on-the-record",
    number: "11",
    title: "On the record",
    provenance: "Original Verbalore",
    craft: ["principled dissent", "on-record", "cooperation without consent"],
    size: "deep",
    minutes: 11,
    context: "Standing against a decision the group has made",
    skill: "Recording dissent without sabotaging the group or grandstanding",
    situation:
      "Your team has settled on a decision you believe is wrong on principle, and the room has moved on. Speaking up costs you standing, but silence reads as consent. You want to register real dissent without blowing up the room or performing it.",
    tangledDraft:
      "I’m happy to go along with whatever everyone thinks is best, it’s fine, I don’t want to be difficult, so ignore me, but I sort of had a small worry.",
    intention: "Put your principled objection on the record without derailing the decision or performing it.",
    invariant: "Your dissent is stated and on the record. You are not sabotaging the group or making it about yourself.",
    concept: "Dissent recorded is not dissent weaponized. You can withhold consent without withholding cooperation.",
    practiceCue: "Separate the sentence that cooperates from the one that objects. Say both, and let neither cancel the other.",
    stances: [
      {
        id: "direct",
        label: "Direct",
        line: "I want it on the record that I think this is the wrong call, and I’ll explain why once.",
        landing: "On record, no fight",
        protects: "The objection is documented without a fight.",
        tradeoff: "Going on record can read as adversarial even when it isn’t.",
      },
      {
        id: "tactful",
        label: "Tactful",
        line: "I’ll go with the group, but I don’t want my silence read as agreement.",
        landing: "Cooperate, not consent",
        protects: "Cooperation and dissent are cleanly separated.",
        tradeoff: "The concession can dilute how seriously the objection is taken.",
      },
      {
        id: "warm-authority",
        label: "Warm authority",
        line: "I respect where the team landed. My objection remains, and I want it recorded before I help carry the decision out.",
        landing: "Loyal and dissenting",
        protects: "Loyalty and dissent hold together.",
        tradeoff: "The warmth can make the objection easy to wave through.",
      },
      {
        id: "restrained",
        label: "Restrained",
        line: "For the record, I remain opposed.",
        landing: "Quiet marker",
        protects: "A quiet marker that costs the room nothing.",
        tradeoff: "The understatement makes it easy to overlook later.",
      },
      {
        id: "punchy",
        label: "Punchy",
        line: "Note my objection. I am against this.",
        landing: "Logged in one stroke",
        protects: "The dissent is logged in a single stroke.",
        tradeoff: "The sharpness can feel aimed at people, not the plan.",
      },
    ],
  },
  {
    id: "light-and-still-standing",
    number: "12",
    title: "Light, and still standing",
    provenance: "Original Verbalore",
    craft: ["wit", "poise", "de-escalation"],
    size: "quick",
    minutes: 4,
    context: "Answering a jab without escalating",
    skill: "Using wit to hold your ground while keeping the tone light",
    situation:
      "You propose one final review to prevent repeated rework. A colleague jokes, ‘You do enjoy adding steps.’ You want to answer lightly while preserving the point that one review now prevents several rescues later.",
    tangledDraft:
      "Ha, yeah, I guess that’s a fair point, I mean you’re probably right, I don’t really have a good comeback for that, sorry.",
    intention: "Answer the jab lightly while preserving the case for one review now to prevent repeated rework later.",
    invariant: "The review remains necessary because it prevents more work later. The answer stays light, not hostile.",
    concept: "Wit is control, not cruelty. The best line leaves everyone free to laugh, including the person you answered.",
    practiceCue: "Deliver each comeback with a smile in your voice. If it only works said coldly, it’s a jab, not wit.",
    stances: [
      {
        id: "direct",
        label: "Direct",
        line: "True, especially when one step prevents three rounds of rework.",
        landing: "Playful hit",
        protects: "You answer squarely while keeping it playful.",
        tradeoff: "Even when delivered lightly, a direct answer can raise the temperature.",
      },
      {
        id: "tactful",
        label: "Tactful",
        line: "Only the steps that save us walking backwards later.",
        landing: "Deflected, not conceded",
        protects: "You deflect without conceding the point.",
        tradeoff: "The softness can let the jab stand unanswered.",
      },
      {
        id: "warm-authority",
        label: "Warm authority",
        line: "Guilty, but this review buys us less rework, and that helps everyone.",
        landing: "Generous and firm",
        protects: "Generosity and confidence in the same breath.",
        tradeoff: "Agreeing first can blur that you held your ground.",
      },
      {
        id: "restrained",
        label: "Restrained",
        line: "One review now, or several rescues later.",
        landing: "Unbothered",
        protects: "Minimal, unbothered, and still standing.",
        tradeoff: "The understatement can read as not engaging at all.",
      },
      {
        id: "punchy",
        label: "Punchy",
        line: "One review. Three rescues avoided.",
        landing: "The arithmetic answers",
        protects: "The comeback and the point land in one compact contrast.",
        tradeoff: "The snap can tip into sharp if the room’s already tense.",
      },
    ],
  },
];

export const ALL_EXPRESSION_STUDIES: ExpressionStudy[] = [
  BOUNDARY_STUDY,
  ...EXPRESSION_STUDIES,
];

export const EXPRESSION_STUDY_BY_ID = new Map(
  ALL_EXPRESSION_STUDIES.map((study) => [study.id, study]),
);
